import {
    AMC_SCAN_OPTIONS,
    BOOLEAN_OPTIONS,
    CREDIT_AUTHORIZATION_OPTIONS,
    EVENT_LABELS,
    PATIENT_STATUS_LABELS,
} from "../model/administrationConstants";
import {
    formatDate,
    latestEvent,
    normalizeAmcScanReceived,
    normalizeCreditAuthorization,
    normalizePatientStatus,
} from "../model/administrationRules";
import {
    backdropStyle,
    closeButtonStyle,
    eventButtonStyle,
    modalHeaderStyle,
    modalStyle,
} from "../styles/administrationModalStyles";
import { inputStyle, patientStatusBadgeStyle } from "../styles/administrationStyles";
import ModalBackdrop from "../../../components/ModalBackdrop";

export function PatientStatusBadge({ status }) {
    const normalized = normalizePatientStatus(status);
    if (normalized === "AUCUN") return null;
    return <span style={patientStatusBadgeStyle}>{PATIENT_STATUS_LABELS[normalized]}</span>;
}

export function PatientInactiveBadge({ inactive }) {
    if (!inactive) return null;
    return <span style={patientStatusBadgeStyle}>Inactif</span>;
}

export function SelectField({ value, options, onChange }) {
    return (
        <select style={inputStyle} value={value ?? ""} onChange={event => onChange(event.target.value || null)}>
            {options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
    );
}

export function BooleanField({ value, onChange }) {
    return (
        <SelectField
            value={value === null || value === undefined ? "" : String(value)}
            options={BOOLEAN_OPTIONS}
            onChange={value => onChange(value === null ? null : value === "true")}
        />
    );
}

export function CreditAuthorizationField({ row, onChange }) {
    return (
        <SelectField
            value={normalizeCreditAuthorization(row)}
            options={CREDIT_AUTHORIZATION_OPTIONS}
            onChange={value => onChange(value)}
        />
    );
}

export function SubcategoryField({ value, options, onChange }) {
    const normalizedOptions = Array.isArray(options) ? options : [];
    return (
        <select
            style={{ ...inputStyle, opacity: normalizedOptions.length === 0 ? 0.65 : 1 }}
            value={value ?? ""}
            onChange={event => onChange(event.target.value || null)}
            disabled={normalizedOptions.length === 0}
        >
            <option value="">Aucune sous-catégorie</option>
            {normalizedOptions.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
    );
}

export function AmcScanField({ value, onChange }) {
    return (
        <SelectField
            value={normalizeAmcScanReceived(value)}
            options={AMC_SCAN_OPTIONS}
            onChange={selected => onChange(selected)}
        />
    );
}

export function DateField({ value, onChange, label, disabled = false }) {
    return (
        <input
            aria-label={label}
            type="date"
            style={{
                ...inputStyle,
                opacity: disabled ? 0.55 : 1,
                background: disabled ? "#f1f5f9" : inputStyle.background,
                cursor: disabled ? "not-allowed" : "default",
            }}
            value={value || ""}
            disabled={disabled}
            onChange={event => onChange(event.target.value || null)}
        />
    );
}

export function AmountField({ value, onChange, label, disabled = false }) {
    return (
        <input
            aria-label={label}
            type="number"
            min="0"
            step="0.01"
            style={{
                ...inputStyle,
                minWidth: "120px",
                opacity: disabled ? 0.55 : 1,
                background: disabled ? "#f1f5f9" : inputStyle.background,
                cursor: disabled ? "not-allowed" : "default",
            }}
            value={value ?? ""}
            disabled={disabled}
            onChange={event => onChange(event.target.value)}
        />
    );
}

export function CheckboxField({ checked, onChange, label }) {
    return (
        <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: "#334155", fontSize: "13px", fontWeight: 600 }}>
            <input
                aria-label={label}
                type="checkbox"
                checked={!!checked}
                onChange={event => onChange(event.target.checked)}
            />
            <span>Oui</span>
        </label>
    );
}

export function TextField({ value, onChange, multiline = false, label }) {
    if (multiline) {
        return (
            <textarea
                aria-label={label}
                rows={3}
                style={{ ...inputStyle, resize: "vertical", minWidth: "180px" }}
                value={value || ""}
                onChange={event => onChange(event.target.value)}
            />
        );
    }
    return <input aria-label={label} style={inputStyle} value={value || ""} onChange={event => onChange(event.target.value)} />;
}

export function EventCell({ row, type, vaccination = false, onOpen }) {
    const event = latestEvent(row, type, vaccination);
    const label = event
        ? `${EVENT_LABELS[type] || "Événement"} ${formatDate(event.date)}`
        : `${EVENT_LABELS[type] || "Événement"} aucune date`;
    return (
        <button type="button" aria-label={label} onClick={() => onOpen(row, type, vaccination)} style={eventButtonStyle}>
            <span>{event ? formatDate(event.date) : "Aucune date"}</span>
            {event?.description && <span style={{ color: "#4b5563", fontSize: "11px" }}>{event.description}</span>}
            <span style={{ color: "#4f46e5", fontSize: "11px" }}>
                {(vaccination ? row.vaccinationEvents : row.billingEvents)?.filter(item => item.type === type).length || 0} entrée(s)
            </span>
            <span style={{ color: "#6366f1", fontSize: "11px", fontWeight: 600 }}>Modifier / historique</span>
        </button>
    );
}

export function Modal({ title, children, onClose, width = 620, zIndex }) {
    return (
        <div style={zIndex ? { ...backdropStyle, zIndex } : backdropStyle}>
            <ModalBackdrop onClose={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                style={{ ...modalStyle, width, position: "relative", zIndex: 1 }}
            >
                <div style={modalHeaderStyle}>
                    <h2 style={{ margin: 0, fontSize: "18px" }}>{title}</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer" style={closeButtonStyle}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}

export function EyeIcon() {
    return (
        <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}
